export const MAP_SHARE_MAX_BYTES = 32 * 1024 * 1024;
export const MAP_SHARE_ID_PATTERN = /^[A-Za-z0-9_-]{32}$/;
export const MAP_SHARE_REVOKE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
export const MAP_SHARE_PATH_PATTERN = /^\/maps\/([A-Za-z0-9_-]{32})$/;

export interface MapShareCreated {
  id: string;
  expiresAt: number;
  revokeToken: string;
}

export function parsePublicMapPath(pathname: string): string | undefined {
  return MAP_SHARE_PATH_PATTERN.exec(pathname)?.[1];
}

function requireMapShareId(id: string) {
  if (!MAP_SHARE_ID_PATTERN.test(id)) throw new Error("The shared map link is invalid.");
  return id;
}

export function mapShareImageUrl(id: string) {
  return `/api/map-shares/${requireMapShareId(id)}`;
}

function mapShareError(status: number) {
  if (status === 413) return "This map is too large to share.";
  if (status === 429) return "Too many maps were shared recently. Try again later.";
  if (status === 507) return "The temporary map-share service is full. Try again later.";
  return "The map could not be shared.";
}

function parsedCreatedShare(value: unknown): MapShareCreated | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const candidate = value as Partial<MapShareCreated>;
  if (typeof candidate.id !== "string" || !MAP_SHARE_ID_PATTERN.test(candidate.id)) return undefined;
  if (typeof candidate.revokeToken !== "string" || !MAP_SHARE_REVOKE_TOKEN_PATTERN.test(candidate.revokeToken)) return undefined;
  if (typeof candidate.expiresAt !== "number" || !Number.isSafeInteger(candidate.expiresAt) || candidate.expiresAt <= 0) return undefined;
  return { id: candidate.id, expiresAt: candidate.expiresAt, revokeToken: candidate.revokeToken };
}

export async function publishMapShare(png: Blob): Promise<MapShareCreated> {
  if (png.type !== "image/png") throw new Error("Only a rendered PNG can be shared.");
  if (!png.size) throw new Error("The rendered PNG is empty.");
  if (png.size > MAP_SHARE_MAX_BYTES) throw new Error("This map is too large to share.");
  const response = await fetch("/api/map-shares", {
    method: "POST",
    headers: { "content-type": "image/png" },
    body: png,
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(mapShareError(response.status));
  const created = parsedCreatedShare(await response.json());
  if (!created) throw new Error("The map-share service returned an invalid response.");
  return created;
}

export async function revokeMapShare(id: string, revokeToken: string): Promise<void> {
  requireMapShareId(id);
  if (!MAP_SHARE_REVOKE_TOKEN_PATTERN.test(revokeToken)) throw new Error("The map-share revoke token is invalid.");
  const response = await fetch(mapShareImageUrl(id), {
    method: "DELETE",
    headers: { authorization: `Bearer ${revokeToken}` },
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error("The shared map could not be revoked.");
}
